import React, { useEffect, useMemo } from 'react';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import {
  CurrentUserInfoContext,
  ICurrentUserInfoContext,
} from '../../../services/contexts/CurrentUserInfoContext';
import {
  checkIsOrganization,
  ExternalUserRole,
  PersonRole,
  ToggleDisableExternalUsers,
} from '../../../constants/permissions';

export const CurrentUserInfoProvider: React.FC<{ children: any }> = ({ children }) => {
  const currentUser = useCurrentUser();

  const enableExternalUsers = useMemo(() => {
    const toggles = window?.sessionStorage?.getItem('toggles') as string;
    if (!toggles) return false;
    const togglesObject = JSON.parse(toggles);
    return !isToggleOn(togglesObject, ToggleDisableExternalUsers, null);
  }, []);

  const { isOrganization, isPerson, isExternal } = useMemo(() => {
    const role = currentUser?.profile?.role;
    const hasOrgRole = checkIsOrganization(currentUser);
    const hasExternalRole = role?.includes(ExternalUserRole);
    const hasPersonRole = role?.includes(PersonRole);
    return {
      isOrganization: hasOrgRole,
      isPerson: hasPersonRole || hasExternalRole,
      isExternal: !hasPersonRole && hasExternalRole && enableExternalUsers && !hasOrgRole,
    };
  }, [currentUser?.profile?.role, enableExternalUsers]);

  useEffect(() => {
    window?.sessionStorage?.setItem('user_type', isExternal ? 'external' : 'other');
  }, [isExternal]);

  const value: ICurrentUserInfoContext = {
    userInfo: { isOrganization, isPerson, isExternal, userLoaded: !!currentUser },
  };

  return (
    <CurrentUserInfoContext.Provider value={value}>{children}</CurrentUserInfoContext.Provider>
  );
};
